import { Project } from '@prisma/client';
import { useEffect } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { LOCAL_STORAGE_KEYS } from './constants';
import { useCurrentProject } from './hooks/useCurrentProject';
import { Loading } from './loading';
import { useGetProjects } from './modules/projects/projects.queries';
import { WorkspacePermissionProvider } from './modules/workspace/workspace-permission-provider';
import { useGetWorkspace, useGetWorkspaces } from './modules/workspace/workspace.queries';
import { useStore } from './store';

export const WorkspaceRoute = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const setCurrentWorkspace = useStore((state) => state.setCurrentWorkspace);
  const setWorkspaces = useStore((state) => state.setWorkspaces);
  const selectWorkspace = useStore((state) => state.selectWorkspace);
  const selectedWorkspaceId = useStore((state) => state.selectedWorkspaceId);
  const allWorkspaces = useStore((state) => state.getAllWorkspaces());
  const logout = useStore((state) => state.logout);
  const { isLoading: isLoadingProjects } = useCurrentProject();

  // Always fetch all workspaces
  const { data: workspaces, isLoading: isLoadingWorkspaces } = useGetWorkspaces();

  // Fetch current workspace if ID is provided
  const { data: workspace, isLoading: isLoadingWorkspace } = useGetWorkspace(
    workspaceId ? Number(workspaceId) : undefined
  );

  // Store workspaces in global state
  useEffect(() => {
    if (workspaces) {
      console.log('Setting workspaces in global state:', workspaces);
      setWorkspaces(workspaces);
    }
  }, [workspaces, setWorkspaces]);

  // Update selected workspace ID when URL changes or when selectedWorkspaceId doesn't match
  useEffect(() => {
    console.log('Workspace selection state:', {
      workspaceId,
      selectedWorkspaceId,
      isMatching: selectedWorkspaceId === Number(workspaceId),
    });

    if (workspaceId && selectedWorkspaceId !== Number(workspaceId)) {
      console.log('Selecting new workspace:', workspaceId);
      selectWorkspace(Number(workspaceId));
    }
  }, [workspaceId, selectedWorkspaceId, selectWorkspace]);

  // Set current workspace when it loads
  useEffect(() => {
    console.log('Current workspace state:', {
      workspace,
      isLoadingWorkspace,
    });

    if (workspace) {
      console.log('Setting current workspace:', workspace);
      setCurrentWorkspace(workspace);
    }
  }, [workspace, setCurrentWorkspace]);

  if (isLoadingWorkspaces || isLoadingWorkspace || isLoadingProjects) {
    console.log('Loading state:', {
      isLoadingWorkspaces,
      isLoadingWorkspace,
      isLoadingProjects,
    });
    return <Loading stage="workspace" />;
  }

  // If no workspaces available, logout
  if (!workspaces || workspaces.length === 0) {
    console.log('No workspaces available, logging out');
    logout();
    return null;
  }

  // If no workspaceId provided or workspace not found, redirect to first workspace
  if (!workspaceId || !workspace) {
    const firstWorkspaceId = allWorkspaces[0]?.id;
    console.log('Redirecting to first workspace:', firstWorkspaceId);
    if (firstWorkspaceId) {
      return <Navigate to={`/${firstWorkspaceId}`} replace />;
    }
  }

  return (
    <WorkspacePermissionProvider>
      <Outlet />
    </WorkspacePermissionProvider>
  );
};
